import "../../components/printPreview/_printPreview.scss";

import { Map } from "immutable";
import PropTypes from "prop-types";
import PubSub from "pubsub-js";
import React, { PureComponent } from "react";
import { connect } from "react-redux";

import ModalHeading from "../../components/modal/ModalHeading";
import ModalView from "../../components/modal/ModalView";
import PrintPreviewBody from "../../components/printPreview/PrintPreviewBody";
import PrintPreviewFooter from "../../components/printPreview/PrintPreviewFooter";
import imageFileFormats from "../../constants/imageFileFormats";
import interfaceMessages from "../../constants/interfaceMessages";
import printDimensions from "../../constants/printing/printDimensions";
import printOrientation from "../../constants/printing/printOrientation";
import PubSubTopics from "../../constants/pubSubTopics";
import Dimensions from "../../constants/styles/dimensions";
import dialogs from "../../dialogs";
import { appStateSelectors } from "../../modules/appState";
import { preferencesSelectors } from "../../modules/preferences";
import { uiStateOperations } from "../../modules/uiState";
import { isNativeCommsInstalled, nativeCommsBroadcastMessageTypes } from "../../nativeComms";
import performPrint from "../../printing";
import defaultPageInfo from "../../printing/defaultPageInfo";
import generatePdfBehaviour from "../../printing/generatePdfBehaviour";
import getImageList from "../../printing/getImageList";
import { immutableRecords } from "../../types";
import { mergeDispatchToProps } from "../../utils/codeSplit";
import ExportImagePreviewOptions from "./ExportImagePreviewOptions";

const scale = 1.0;
const paperSize = printDimensions.PAPER_SIZE_INFINITE;
const orientation = printOrientation.Landscape;

const getPageInfo = skin => {
  let paperDimensions = printDimensions.paperDimensions[paperSize];

  let customPageInfo = Object.assign(
    {
      paperSize: paperSize,
      scale: scale,
      orientation: orientation,
      leftMargin: 0,
      rightMargin: 0,
      topMargin: 0,
      bottomMargin: 0,
      skin
    },
    paperDimensions
  );

  return Object.assign({}, defaultPageInfo, customPageInfo);
};

class ExportImagePreviewModal extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      base64Data: null,
      imageData: Map(),
      imageDataPending: true,
      isSelectedItemsOnly: false,
      format: imageFileFormats.PDF,
      errorMessage: "",
      pageInfo: getPageInfo(props.skin)
    };

    this._lastRequestState = null;

    this.updateImageData = this.updateImageData.bind(this);
    this.performPrintWithBehaviour = this.performPrintWithBehaviour.bind(this);
    this.previewDataGenerated = this.previewDataGenerated.bind(this);
    this.onChangeSelectedItemsOnly = this.onChangeSelectedItemsOnly.bind(this);
    this.onChangeFormat = this.onChangeFormat.bind(this);
    this.onPrintClick = this.onPrintClick.bind(this);
  }

  componentDidMount() {
    // Register our print function as a callback for when the data is loaded...
    this._pubSubPrintGeneratedToken = PubSub.subscribe(PubSubTopics.printPdfGenerated, this.previewDataGenerated);
    this.requestImageData();
    this._pubSubEscapeKeyDownToken = PubSub.subscribe(PubSubTopics.escapeKeyDown, this.props.onCloseModal);
  }

  componentDidUpdate() {
    if (!this.state.imageDataPending) {
      this.requestPreviewData();
    }
  }

  componentWillUnmount() {
    // Register our print function as a callback for when the data is loaded...
    PubSub.unsubscribe(this._pubSubPrintGeneratedToken);
    PubSub.unsubscribe(this._pubSubPrintWithLoadedImagesToken);
    PubSub.unsubscribe(this._pubSubEscapeKeyDownToken);
  }

  requestImageData() {
    // Find out if we need to load any images
    const imageUrls = getImageList(this.props.localStore);

    // If the list is empty, then we can just print straight away
    if (imageUrls.length === 0) {
      this.updateImageData(null, Map());
      return;
    }

    // If native comms is not installed, then we just print straight away
    // without images
    if (!isNativeCommsInstalled()) {
      dialogs.alertPrompt(interfaceMessages.PRINT_WARNING_NO_IMAGES_IN_BROWSER);
      this.updateImageData(null, Map());
      return;
    }

    // Otherwise, there are images that need to be loaded before printing.
    // First, erase any previous print that is waiting for this data.
    // Register our print function as a callback for when the data is loaded
    this._pubSubPrintWithLoadedImagesToken = PubSub.subscribeOnce(
      PubSubTopics.printWithLoadedImages,
      this.updateImageData
    );

    // And now send an async request to ask for the image data to be loaded.
    PubSub.publish(PubSubTopics.sendNativeMessage, {
      msg: nativeCommsBroadcastMessageTypes.REQUEST_PRINT_IMAGES,
      urls: imageUrls
    });
  }

  updateImageData(_msg, data) {
    this.setState({
      imageData: data,
      imageDataPending: false
    });

    // And we no longer need to unsubscribe since we only subscribed once
    this._pubSubPrintWithLoadedImagesToken = null;
  }

  performPrintWithBehaviour(behaviour) {
    //#!if harvard !== true
// try {
// performPrint(
// this.props.localStore,
// getPageInfo(this.props.skin),
// this.state.imageData,
// behaviour,
// this.state.isSelectedItemsOnly,
// window.fontServerUrl
// );
// } catch (e) {
      // eslint-disable-next-line no-console
// console.error(`PerformPrintWithBehaviour: caught error`);
      // eslint-disable-next-line no-console
// console.error(e);
// this.setState({
// base64Data: null,
// errorMessage: "An error occurred when generating a preview of the printed document."
// });
// }
    //#!endif
  }

  requestPreviewData() {
    if (this._lastRequestState && this._lastRequestState.isSelectedItemsOnly === this.state.isSelectedItemsOnly) {
      return;
    }

    // Grab the current properties out of our state
    const { isSelectedItemsOnly } = this.state;
    // And store them as the last used values
    this._lastRequestState = { isSelectedItemsOnly };
    // Clear our preview data ready for next time
    this.setState({ base64Data: null, errorMessage: "" });
    // And request the PDF generation
    this.performPrintWithBehaviour(generatePdfBehaviour.SEND_TO_PREVIEW);
  }

  previewDataGenerated(msg, data) {
    if (this.state.base64Data != data.base64 || this.state.errorMessage !== data.errorMessage) {
      this.setState({
        base64Data: data.base64,
        pageInfo: data.pageInfo || getPageInfo(this.props.skin),
        errorMessage: data.errorMessage
      });
    }
  }

  onChangeSelectedItemsOnly(value) {
    this.setState({ isSelectedItemsOnly: value });
  }

  onChangeFormat(value) {
    this.setState({ format: value });
  }

  toImageDataUri() {
    // First, grab the canvas
    const canvasEl = document.querySelector(".printPreviewModal canvas");
    // Now grab a data URI
    return canvasEl.toDataURL(this.state.format);
  }

  toImageData() {
    // Grab the URI
    const dataUri = this.toImageDataUri();
    // Trim off the URI bit so we are left with Base64
    return dataUri.substring(dataUri.indexOf(",") + 1);
  }

  onPrintClick() {
    const formatIsPdf = this.state.format === imageFileFormats.PDF;

    // If we are talking to a browser, then we need to send data through
    if (isNativeCommsInstalled()) {
      let imageData = formatIsPdf ? this.state.base64Data : this.toImageData();
      const data = {
        msg: nativeCommsBroadcastMessageTypes.EXPORT_DATA_IMAGE,
        data: imageData,
        format: this.state.format
      };
      PubSub.publish(PubSubTopics.sendNativeMessage, data);
    } else {
      if (formatIsPdf) {
        // Otherwise, we want to open it in the browser
        this.performPrintWithBehaviour(generatePdfBehaviour.OPEN);
      } else {
        const dataUri = this.toImageDataUri();
        window.open(dataUri);
      }
    }

    // if (isWindowsApp) {
    //   // The Windows app just calls print() and let's CEF3 handle the
    //   // opening and print dialog.
    //   // Need to re-generate the PDF in order to do it though
    //   this.performPrintWithBehaviour(generatePdfBehaviour.PRINT);
    // } else if (!isWindowsApp && isNativeCommsInstalled()) {
    //   // Mac: send through to native and it will do the printing
    //   // for us.
    //   // const data = {
    //   //   msg: nativeCommsBroadcastMessageTypes.EXPORT_DATA_PDF,
    //   //   pdfData: this.state.base64Data,
    //   //   pageInfo: this.state.pageInfo
    //   // };
    //   // PubSub.publish(PubSubTopics.sendNativeMessage, data);

    //   // Find the canvas
    //   const canvasEl = document.querySelector(".printPreviewModal canvas");
    //   const MIME_TYPE = "image/png";
    //   const imgURL = canvasEl.toDataURL(MIME_TYPE);
    //   const imgBase64 = imgURL.substring(imgURL.indexOf(",") + 1);

    //   console.log(`onPrintClick(): canvasEl = ${canvasEl}, imgURL = ${imgURL}`);
    //   console.log(`imgBase64: ${imgBase64}`);

    //   const data = {
    //     msg: nativeCommsBroadcastMessageTypes.EXPORT_DATA_PDF,
    //     pdfData: imgBase64
    //   };
    //   PubSub.publish(PubSubTopics.sendNativeMessage, data);
    // } else {
    //   // Open the PDF file in the browser
    //   // Need to re-generate the PDF in order to do it though
    //   this.performPrintWithBehaviour(generatePdfBehaviour.OPEN);
    // }
    this.props.onCloseModal();
  }

  render() {
    const modalHeading = (
      <ModalHeading title="Export Image" showCancel={true} onCancelClick={this.props.onCloseModal} />
    );
    const modalFooter = (
      <PrintPreviewFooter
        buttonLabel="Export"
        onPrintClick={this.onPrintClick}
        canPrint={Boolean(this.state.base64Data)}
      />
    );

    const previewOptions = (
      <ExportImagePreviewOptions
        isSelectedItemsOnly={this.state.isSelectedItemsOnly}
        hasSelectedItem={this.props.hasSelectedItem}
        onChangeSelectedItemsOnly={this.onChangeSelectedItemsOnly}
        format={this.state.format}
        onChangeFormat={this.onChangeFormat}
        pageInfo={this.state.pageInfo}
      />
    );

    return (
      <ModalView
        className="printPreviewModal"
        header={modalHeading}
        footer={modalFooter}
        fixedHeight={Dimensions.MODAL_MAX_HEIGHT}
      >
        <PrintPreviewBody
          pageInfo={this.state.pageInfo}
          base64Data={this.state.base64Data}
          showFullSize={Boolean(this.state.base64Data)}
          errorMessage={this.state.errorMessage}
          previewOptions={previewOptions}
          showNavigation={false}
        />
      </ModalView>
    );
  }
}

const mapStateToProps = (state, _props) => {
  return {
    localStore: state.present,
    hasSelectedItem: appStateSelectors.getSelectedItemCount(state.present) > 0,
    skin: preferencesSelectors.getSkin(state.present)
  };
};

const readonlyActions = {
  onCloseModal: uiStateOperations.hideExportImagePreviewModal
};

const editingActions = {};

const mapDispatchToProps = dispatch => mergeDispatchToProps(readonlyActions, editingActions, dispatch);

ExportImagePreviewModal.propTypes = {
  localStore: PropTypes.instanceOf(immutableRecords.TimelineRecord).isRequired,
  onCloseModal: PropTypes.func.isRequired,
  hasSelectedItem: PropTypes.bool.isRequired,
  skin: PropTypes.string.isRequired
};

export default connect(mapStateToProps, mapDispatchToProps)(ExportImagePreviewModal);
